/**
 * ListFacturasCxP.js — Lista de Facturas CxP (FacturaCxP)
 * Mismo estilo exacto que ListInvoices de CxC:
 * - invoice.module.css clases
 * - ActionBtn idéntico al de InvoiceRow
 * - Columna Gasto/Período igual que Rubro/Período de CxC
 * - Fecha DD/MM/YYYY
 * - Monto text-end monospace
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import ImportExportBar from "../../shared/ImportExportBar";
import { TableToolbar, FilterField, FilterInput, FilterSelect, SortButton } from "../../shared/TableToolbar";
import { IvaSelector } from "../../components/IvaSelector";
import EmitirNotaCxPModal from "../components/EmitirNotaCxPModal";
import RowActionsMenu from "../../../../shared/components/RowActionsMenu";
import RubrosModal from "../../../../shared/components/RubrosModal";
import { useEsCliente } from "../../../../shared/hooks/useTenantTipo";
import { SearchSelect } from "../../../../shared/components/SearchSelect";
import AccountingDetailModal from "../../shared/AccountingDetailModal";
import { overlayClose } from "../../../../shared/utils/overlayClose";

/** Extrae el id de cuenta, venga como objeto anidado (lectura) o como id plano. */
function cuentaIdDe(r) {
  return typeof r.cuenta_gasto === "object" && r.cuenta_gasto !== null
    ? r.cuenta_gasto.id
    : r.cuenta_gasto;
}

/** "2026-04-15" → "15/04/2026" */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

function getMes(concepto, fecha) {
  const c = (concepto || "").toLowerCase();
  const idx = MESES.findIndex(m => c.includes(m.toLowerCase()));
  if (idx !== -1) return MESES[idx];
  if (fecha?.length >= 7) return MESES[parseInt(fecha.split("-")[1], 10) - 1] ?? "";
  return "";
}

// ── ActionButtons — "Ver" visible + resto agrupado en menú "⋮" ───────────────
function ActionButtons({ onVer, onEdit, onDelete, onEmitirNota, puedeEmitirNota, t }) {
  const btn = (onClick, bg, border, color, icon, title) => (
    <button type="button" onClick={onClick} title={title} style={{
      background: bg, border: `1px solid ${border}`,
      borderRadius: 6, padding: "4px 10px",
      color, cursor: "pointer",
    }}>
      <i className={`fa-solid ${icon}`} />
    </button>
  );
  return (
    <div className="d-flex align-items-center justify-content-center" style={{ gap: 6 }}>
      {onVer && btn(onVer, "rgba(96,165,250,0.1)", "rgba(96,165,250,0.3)", "#60a5fa", "fa-eye", t("cxp.verDetalle"))}
      <RowActionsMenu items={[
        onEdit && { icon: "fa-square-pen", label: t("common.editar"), color: "#f97316", onClick: onEdit },
        (puedeEmitirNota && onEmitirNota) && { icon: "fa-file-pen", label: t("notas.emitir"), color: "#a78bfa", onClick: onEmitirNota },
        onDelete && { icon: "fa-trash", label: t("common.eliminar"), color: "#f87171", onClick: onDelete },
      ]}/>
    </div>
  );
}

// ── Fila — theme-aware ────────────────────────────────────────────────────────
function FacturaCxPRow({ fac, onVer, onEdit, onDelete, onEmitirNota, puedeEmitirNota, t }) {
  const nombreGasto = fac.cuenta_gasto?.nombre ?? fac.concepto ?? "—";
  const mes = getMes(fac.concepto, fac.fecha_emision);

  return (
    <tr>
      <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>
        {fac.numero_doc_proveedor || fac.numero_factura || "—"}
      </td>

      <td>
        <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{nombreGasto}</span>
        {mes && (
          <span style={{ display: "block", fontSize: "0.75rem",
            color: "var(--text-secondary)", marginTop: 1 }}>
            {mes}
          </span>
        )}
      </td>

      <td style={{ fontSize: 15 }}>
        {fac.cuenta_proveedor?.nombre ?? "—"}
      </td>

      <td>{fmtDate(fac.fecha_emision)}</td>

      <td>
        <div style={{ textAlign: "right", fontFamily: "Inter", fontWeight: 300 }}>
          {(fac.moneda || 'CRC').toUpperCase() === 'USD' ? (
            <>
              <span style={{ color: "#60a5fa" }}>${Number(fac.monto).toFixed(2)}</span>
              {Number(fac.monto_colones) > 0 && (
                <span style={{ display: "block", fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 1 }}>
                  ≈ ₡{formatCurrency(fac.monto_colones)}
                </span>
              )}
            </>
          ) : (
            <>₡{formatCurrency(fac.monto)}</>
          )}
        </div>
      </td>

      <td className="text-center">
        <ActionButtons
          onVer={onVer}
          onEdit={onEdit}
          onDelete={onDelete}
          onEmitirNota={onEmitirNota}
          puedeEmitirNota={puedeEmitirNota}
          t={t}
        />
      </td>
    </tr>
  );
}

// ── Modal editar factura CxP — theme-aware ────────────────────────────────────
const OVERLAY_F = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  backdropFilter: "blur(3px)", zIndex: 1000,
};
const MODAL_F = {
  position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
  background: "var(--bg-card)", border: "1px solid var(--border-card)",
  borderRadius: 12, padding: 24,
  width: "min(540px,94vw)", zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
  maxHeight: "90vh", overflowY: "auto",
};
const LABEL_F = {
  display: "block", fontSize: "0.78rem", fontWeight: 600,
  color: "var(--text-secondary)", marginBottom: 5,
};
const INP_F = {
  width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-input)",
  borderRadius: 8, padding: "9px 12px", color: "var(--text-primary)", fontSize: "0.9rem",
  outline: "none", boxSizing: "border-box", transition: "border-color 0.15s",
};

function EditFacturaModal({ fac, onClose, onSaved, t }) {
  const esCliente = useEsCliente();
  const [cuentasGasto,     setCuentasGasto]     = useState([]);
  const [cuentasPasivo,    setCuentasPasivo]    = useState([]);
  const [showRubrosModal,  setShowRubrosModal]  = useState(false);
  const [form, setForm] = useState({
    numero_doc_proveedor: fac.numero_doc_proveedor ?? "",
    concepto:             fac.concepto ?? "",
    fecha_emision:        fac.fecha_emision ?? "",
    fecha_vencimiento:    fac.fecha_vencimiento ?? "",
    // fac.monto es el TOTAL; el campo guarda el digitado (base si SUMAR, total si INCLUIDO).
    monto:                String(
                            (fac.iva_modo ?? "SUMAR") === "INCLUIDO"
                              ? (Number(fac.monto) || 0)
                              : (Number(fac.monto) || 0) - (Number(fac.monto_iva) || 0)
                          ),
    cuenta_gasto:         fac.cuenta_gasto?.id ?? "",
    cuenta_proveedor:     fac.cuenta_proveedor?.id ?? "",
    iva_incluido:         fac.iva_incluido ?? Number(fac.monto_iva) > 0,
    tasa_iva:             Number(fac.tasa_iva) || 13,
    iva_modo:             fac.iva_modo ?? "SUMAR",
    rubros:               fac.rubros ?? [],
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  function aplicarRubros(rubrosData, total) {
    set("rubros", rubrosData);
    set("monto", String(total));
    set("cuenta_gasto", rubrosData[0].cuenta_gasto);
    setShowRubrosModal(false);
  }

  function quitarRubros() {
    set("rubros", []);
  }

  useEffect(() => {
    apiFetch("/api/finance/catalogo/detalle/?tipo=GASTO&page_size=500")
      .then(r => r.json()).then(d => setCuentasGasto(Array.isArray(d) ? d : (d.results ?? [])));
    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&excluir_a_favor=1&bajo_relacion=proveedores&page_size=500")
      .then(r => r.json()).then(d => setCuentasPasivo(Array.isArray(d) ? d : (d.results ?? [])));
  }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.numero_doc_proveedor.trim()) { setError("El N° Doc Proveedor es requerido."); return; }
    if (!form.concepto.trim())             { setError("El concepto es requerido."); return; }
    if (!form.fecha_emision)               { setError("La fecha de emisión es requerida."); return; }
    if (Number(form.monto) <= 0)           { setError("El monto debe ser mayor a 0."); return; }
    if (!form.cuenta_gasto)                { setError("Seleccioná la cuenta de gasto."); return; }
    if (!form.cuenta_proveedor)            { setError("Seleccioná la cuenta del proveedor."); return; }
    setSaving(true); setError("");
    try {
      const res = await apiFetch(`/api/finance/cxp/${fac.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          numero_doc_proveedor: form.numero_doc_proveedor.trim(),
          concepto:             form.concepto.trim(),
          fecha_emision:        form.fecha_emision,
          fecha_vencimiento:    form.fecha_vencimiento || null,
          monto:                Number(form.monto), // BASE — el backend agrega el IVA
          iva_incluido:         form.iva_incluido,
          tasa_iva:             form.iva_incluido ? Number(form.tasa_iva) : 0,
          iva_modo:             form.iva_modo,
          cuenta_gasto:         form.cuenta_gasto ? Number(form.cuenta_gasto) : null,
          cuenta_proveedor:     form.cuenta_proveedor ? Number(form.cuenta_proveedor) : null,
          rubros: form.rubros.map((r) => ({
            cuenta_gasto: cuentaIdDe(r),
            concepto: r.concepto || "",
            monto: Number(r.monto),
          })),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('PATCH /cxp/ error:', JSON.stringify(err));
        const msgs = Object.entries(err).map(([k,v]) =>
          `${k}: ${Array.isArray(v) ? v[0] : v}`
        );
        throw new Error(msgs.join(' | ') || 'Error al actualizar la factura.');
      }
      const updated = await res.json();
      onSaved("Factura actualizada correctamente.", updated);
      onClose();
    } catch (ex) { setError(ex.message); }
    finally { setSaving(false); }
  }

  const field = (label, children, span2 = false) => (
    <div style={span2 ? { gridColumn: "span 2" } : {}}>
      <label style={LABEL_F}>{label}</label>
      {children}
    </div>
  );

  return (
    <>
      <div {...overlayClose(onClose)} style={OVERLAY_F}/>
      <div style={MODAL_F}>
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 16, paddingBottom: 12,
          borderBottom: "1px solid var(--border-card)",
        }}>
          <div>
            <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
              <i className="fa-solid fa-square-pen" style={{ color: "#f97316", marginRight: 8 }}/>
              {t("cxp.editarFactura")}
            </h3>
            <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: "0.8rem" }}>
              {fac.numero_factura} · {t("cxp.asientoActualizado")}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none",
            color: "var(--text-muted)", cursor: "pointer", fontSize: "1.2rem" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
          {field(`${t("cxp.numDocProveedor")} *`,
            <input type="text" style={INP_F} value={form.numero_doc_proveedor}
              onChange={e => set("numero_doc_proveedor", e.target.value)}
              onFocus={e => e.target.style.borderColor="#f97316"}
              onBlur={e  => e.target.style.borderColor="var(--border-input)"}
              placeholder="Ej: F-12345"/>
          )}
          {field(
            `${t("common.monto")} ${form.iva_incluido && form.iva_modo === "INCLUIDO" ? "(total, IVA incluido)" : "(base, sin IVA)"} *`,
            <>
              <input type="number" min="0.01" step="0.01" style={{
                ...INP_F,
                fontFamily: "monospace",
                ...(form.rubros.length > 0 ? { opacity: 0.7, cursor: "not-allowed" } : {}),
              }} value={form.monto}
                disabled={form.rubros.length > 0}
                onChange={e => set("monto", e.target.value)}
                onFocus={e => e.target.style.borderColor="#f97316"}
                onBlur={e  => e.target.style.borderColor="var(--border-input)"}/>
              {form.rubros.length > 0 && (
                <div style={{ fontSize: "0.73rem", color: "var(--text-muted)", marginTop: 3 }}>
                  {t("rubros.montoCalculado", { n: form.rubros.length })}
                </div>
              )}
            </>
          )}
          {field("IVA",
            <IvaSelector
              ivaIncluido={form.iva_incluido}
              tasaIva={form.tasa_iva}
              onIvaIncluidoChange={(v) => set("iva_incluido", v)}
              onTasaChange={(v) => set("tasa_iva", v)}
              base={Number(form.monto) || 0}
              moneda={(fac.moneda || "CRC").toUpperCase()}
              modo={form.iva_modo || "SUMAR"}
              onModoChange={(v) => set("iva_modo", v)}
            />,
            true,
          )}
          {field(`${t("cxp.fechaEmision")} *`,
            <input type="date" style={INP_F} value={form.fecha_emision}
              onChange={e => set("fecha_emision", e.target.value)}
              onFocus={e => e.target.style.borderColor="#f97316"}
              onBlur={e  => e.target.style.borderColor="var(--border-input)"}/>
          )}
          {field(t("cxp.fechaVencimiento"),
            <input type="date" style={INP_F} value={form.fecha_vencimiento}
              onChange={e => set("fecha_vencimiento", e.target.value)}
              onFocus={e => e.target.style.borderColor="#f97316"}
              onBlur={e  => e.target.style.borderColor="var(--border-input)"}/>
          )}
          {field(`${t("cxp.cuentaGasto")} *`,
            form.rubros.length > 0 ? (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "var(--bg-input)", border: "1px solid var(--border-card)",
                borderRadius: 6, padding: "8px 10px", gap: 8, flexWrap: "wrap",
              }}>
                <span style={{ fontSize: "0.85rem", color: "var(--text-primary)", whiteSpace: "nowrap" }}>
                  <i className="fa-solid fa-list-ul" style={{ color: "#f97316", marginRight: 8 }} />
                  {form.rubros.length} {t("rubros.titulo").toLowerCase()}
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button type="button" onClick={() => setShowRubrosModal(true)}
                    style={{ padding: "4px 10px", background: "rgba(96,165,250,0.1)",
                      border: "1px solid rgba(96,165,250,0.3)", borderRadius: 6,
                      color: "#60a5fa", fontSize: "0.78rem", cursor: "pointer" }}>
                    {t("common.editar")}
                  </button>
                  <button type="button" onClick={quitarRubros}
                    style={{ padding: "4px 10px", background: "rgba(248,113,113,0.1)",
                      border: "1px solid rgba(248,113,113,0.3)", borderRadius: 6,
                      color: "#f87171", fontSize: "0.78rem", cursor: "pointer" }}>
                    {t("rubros.quitarRubros")}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <SearchSelect
                  icon="fa-tags"
                  options={cuentasGasto.map(c => ({ id: c.id, primary: c.codigo, secondary: c.nombre }))}
                  value={form.cuenta_gasto ? Number(form.cuenta_gasto) : null}
                  onChange={(id) => set("cuenta_gasto", id ? String(id) : "")}
                  placeholder="Seleccioná cuenta de gasto..."
                />
                {esCliente && (
                  <button type="button" onClick={() => setShowRubrosModal(true)}
                    style={{ marginTop: 6, padding: "5px 10px", background: "transparent",
                      border: "1px dashed var(--border-card)", borderRadius: 6,
                      color: "var(--text-secondary)", fontSize: "0.76rem", cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <i className="fa-solid fa-list-ul" />
                    {t("rubros.botonMultiplesRubros")}
                  </button>
                )}
              </>
            ),
            form.rubros.length > 0,
          )}
          {field(`${t("cxp.cuentaProveedor")} *`,
            <SearchSelect
              icon="fa-building"
              options={cuentasPasivo.map(c => ({ id: c.id, primary: c.codigo, secondary: c.nombre }))}
              value={form.cuenta_proveedor ? Number(form.cuenta_proveedor) : null}
              onChange={(id) => set("cuenta_proveedor", id ? String(id) : "")}
              placeholder="Seleccioná proveedor…"
            />
          )}
          {field(`${t("common.descripcion")} *`,
            <input type="text" style={INP_F} value={form.concepto}
              onChange={e => set("concepto", e.target.value)}
              onFocus={e => e.target.style.borderColor="#f97316"}
              onBlur={e  => e.target.style.borderColor="var(--border-input)"}
              placeholder="Ej: Servicio contable — Abril 2026"/>,
            true
          )}
        </div>

        {error && (
          <div style={{
            marginTop: 12, padding: "10px 14px",
            background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 8, color: "#f87171", fontSize: "0.84rem",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <i className="fa-solid fa-circle-exclamation"/>{error}
          </div>
        )}

        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16,
          paddingTop: 12, borderTop: "1px solid var(--border-card)",
        }}>
          <button onClick={onClose} disabled={saving} style={{
            padding: "8px 16px",
            background: "transparent", border: "1px solid var(--border-card)",
            borderRadius: 7, color: "var(--text-secondary)", fontSize: "0.84rem", cursor: "pointer",
          }}>
            {t("common.cancelar")}
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "8px 20px",
            background: saving ? "rgba(249,115,22,0.5)" : "#f97316",
            color: "#fff", border: "none", borderRadius: 7, fontSize: "0.84rem",
            fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 7,
          }}>
            {saving
              ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("cxp.guardando")}</>
              : <><i className="fa-solid fa-floppy-disk"/> {t("cxp.guardarCambios")}</>}
          </button>
        </div>
      </div>

      <RubrosModal
        open={showRubrosModal}
        onClose={() => setShowRubrosModal(false)}
        tipo="GASTO"
        initialRubros={form.rubros}
        onConfirm={aplicarRubros}
      />
    </>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ListFacturasCxP() {
  const { t } = useTranslation();
  // Emitir notas (crédito/débito): contador, administrador (USUARIO) y superusuarios.
  const _rol = localStorage.getItem("rol") || "USUARIO";
  const _isSuper = localStorage.getItem("is_superuser") === "true";
  const puedeEmitirNota = _isSuper || _rol !== "RESIDENTE";
  const [items,        setItems]        = useState([]);
  const [count,        setCount]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [search,       setSearch]       = useState("");
  const [fechaDesde,   setFechaDesde]   = useState("");
  const [fechaHasta,   setFechaHasta]   = useState("");
  const [montoMin,     setMontoMin]     = useState("");
  const [montoMax,     setMontoMax]     = useState("");
  const [rubro,        setRubro]        = useState("");
  const [rubros,       setRubros]       = useState([]);
  const [total,        setTotal]        = useState(0);
  const [ordering,     setOrdering]     = useState("");
  const [loading,      setLoading]      = useState(true);
  const [deleting,     setDeleting]     = useState(null);
  const [delLoad,      setDelLoad]      = useState(false);
  const [actionMsg,    setActionMsg]    = useState(null);
  const [detalle,      setDetalle]      = useState(null);
  const [editando,     setEditando]     = useState(null);
  const [emitiendoNota, setEmitiendoNota] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const PAGE_SIZE  = 20;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const fetchData = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ page, page_size: PAGE_SIZE });
    if (search.trim())      qs.set("search", search.trim());
    if (fechaDesde.trim())  qs.set("fecha_desde", fechaDesde.trim());
    if (fechaHasta.trim())  qs.set("fecha_hasta", fechaHasta.trim());
    if (montoMin.trim())    qs.set("monto_min", montoMin.trim());
    if (montoMax.trim())    qs.set("monto_max", montoMax.trim());
    if (rubro)              qs.set("rubro", rubro);
    if (ordering.trim())    qs.set("ordering", ordering.trim());
    apiFetch(`/api/finance/cxp/?${qs}`)
      .then(r => r.json())
      .then(d => { setItems(d.results ?? []); setCount(d.count ?? 0); })
      .finally(() => setLoading(false));

    // Total facturado del filtro completo (sin paginar)
    const totQs = new URLSearchParams();
    if (search.trim())      totQs.set("search", search.trim());
    if (fechaDesde.trim())  totQs.set("fecha_desde", fechaDesde.trim());
    if (fechaHasta.trim())  totQs.set("fecha_hasta", fechaHasta.trim());
    if (montoMin.trim())    totQs.set("monto_min", montoMin.trim());
    if (montoMax.trim())    totQs.set("monto_max", montoMax.trim());
    if (rubro)              totQs.set("rubro", rubro);
    apiFetch(`/api/finance/cxp/totales/?${totQs}`)
      .then(r => r.json())
      .then(d => setTotal(Number(d.total) || 0))
      .catch(() => {});
  }, [page, search, fechaDesde, fechaHasta, montoMin, montoMax, rubro, ordering]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Cargar rubros (cuentas de gasto) para el filtro — una sola vez
  useEffect(() => {
    apiFetch("/api/finance/catalogo/detalle/?tipo=GASTO&page_size=500")
      .then(r => r.json())
      .then(d => setRubros(Array.isArray(d) ? d : d.results ?? []))
      .catch(() => {});
  }, []);

  function showMsg(ok, text) {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  const handleImportSuccess = useCallback(() => {
    setPage(1);
    fetchData();
  }, [fetchData]);

  function handleImportResult(resultado) {
    setImportResult(resultado);
  }

  async function handleDelete() {
    if (!deleting) return;
    setDelLoad(true);
    try {
      const res = await apiFetch(`/api/finance/cxp/${deleting.id}/`, { method: "DELETE" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Error al eliminar");
      }
      setDeleting(null);
      showMsg(true, `Factura ${deleting.numero_factura} eliminada correctamente.`);
      setItems(prev => prev.filter(f => f.id !== deleting.id));
      setCount(c => c - 1);
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setDelLoad(false);
    }
  }

  return (
    <>
      {/* Breadcrumb */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/cxp">
          <span className={invoiceStyles.inactive}> {t("cxp.titulo")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("sidebar.facturas")}</span>
      </span>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ marginBottom: 6 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("cxp.gestionFacturas")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("cxp.administraFacturas")}</p>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <ImportExportBar
            plantillaUrl="/api/finance/cxp/plantilla-excel/"
            importarUrl="/api/finance/cxp/importar-excel/"
            plantillaFilename="plantilla_facturas_cxp.xlsx"
            onImportSuccess={handleImportSuccess}
            onResult={handleImportResult}
          />
          <Link to="/finanzas/cxp/facturas/agregar" className="btn"
            style={{
              padding: "7px 14px", background: "#f97316", borderRadius: 6,
              color: "#fff", textDecoration: "none", fontWeight: 400,
              fontSize: 14, display: "flex", alignItems: "center", gap: 7, border: "none",
            }}>
            <i className="fa-solid fa-plus" /> {t("cxp.nuevaFacturaCxP")}
          </Link>
        </div>
      </div>

      {/* Resultado de importación */}
      {importResult && (
        <div style={{
          width: "100%", borderRadius: 8, marginBottom: 14,
          background: importResult.ok ? "rgba(74,222,128,0.07)" : "rgba(248,113,113,0.07)",
          border: `1px solid ${importResult.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
          borderLeft: `4px solid ${importResult.ok ? "#4ade80" : "#f87171"}`,
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "10px 16px",
            borderBottom: importResult.errores?.length > 0 ? "1px solid var(--border-card)" : "none",
          }}>
            <span style={{ fontWeight: 600, fontSize: "0.88rem",
              color: importResult.ok ? "#bbf7d0" : "#fecaca" }}>
              {importResult.msg}
            </span>
            <button onClick={() => setImportResult(null)}
              style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "1rem" }}>✕</button>
          </div>
          {importResult.errores?.length > 0 && (
            <div style={{ padding: "10px 16px" }}>
              <details>
                <summary style={{ cursor: "pointer", color: "#fb923c", fontWeight: 600, fontSize: "0.82rem" }}>
                  {importResult.errores.length} {t("cxp.facturas")} con error — clic para ver
                </summary>
                <ul style={{ marginTop: 8, paddingLeft: 18, color: "#fca5a5", fontSize: "0.8rem" }}>
                  {importResult.errores.map((e, i) => (
                    <li key={i}>
                      <strong>Fila {e.fila}:</strong>{" "}
                      {e.errores?.join(" · ") ?? e}
                      {e.advertencia && <span style={{ color: "#fbbf24", marginLeft: 6 }}>(advertencia)</span>}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {actionMsg && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
          border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
          borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#f87171"}`,
          borderRadius: 8, padding: "12px 18px", fontSize: "0.87rem",
          display: "flex", alignItems: "center", gap: 10,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: 380,
        }}>
          <i className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
             style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontSize: "1.1rem" }} />
          <span style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontWeight: 600 }}>
            {actionMsg.text}
          </span>
        </div>
      )}

      {/* Tabla */}
      <div className={invoiceStyles["table-container"]}>

        {/* Barra búsqueda + orden + filtros (mismo set que CxC) */}
        <TableToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder={t("cxp.buscarFacturaPlaceholder")}
          loading={loading}
          sort={
            <SortButton
              state={ordering === "-numero_doc_proveedor" ? "desc" : ordering === "numero_doc_proveedor" ? "asc" : "none"}
              label={ordering === "-numero_doc_proveedor" ? "N° Doc ↓" : ordering === "numero_doc_proveedor" ? "N° Doc ↑" : t("common.ordenar")}
              onToggle={() => {
                setOrdering((c) => c === "" ? "-numero_doc_proveedor" : c === "-numero_doc_proveedor" ? "numero_doc_proveedor" : "");
                setPage(1);
              }}
            />
          }
          activeFilterCount={[fechaDesde, fechaHasta, montoMin, montoMax, rubro].filter(Boolean).length}
          onClearFilters={() => { setFechaDesde(""); setFechaHasta(""); setMontoMin(""); setMontoMax(""); setRubro(""); setPage(1); }}
          filters={
            <>
              <FilterField label={t("common.fechaEmisionDesde")}>
                <FilterInput type="date" value={fechaDesde}
                  onChange={(e) => { setFechaDesde(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.fechaEmisionHasta")}>
                <FilterInput type="date" value={fechaHasta}
                  onChange={(e) => { setFechaHasta(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.montoMinimo")}>
                <FilterInput type="number" min="0" step="1000" placeholder="0" value={montoMin}
                  onChange={(e) => { setMontoMin(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.montoMaximo")}>
                <FilterInput type="number" min="0" step="1000" placeholder={t("common.sinLimite")} value={montoMax}
                  onChange={(e) => { setMontoMax(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.rubroFiltro")}>
                <FilterSelect value={rubro}
                  onChange={(e) => { setRubro(e.target.value); setPage(1); }}>
                  <option value="">{t("common.todosLosRubros")}</option>
                  {rubros.map((r) => (
                    <option key={r.id} value={String(r.id)}>{r.nombre}</option>
                  ))}
                </FilterSelect>
              </FilterField>
            </>
          }
        />

        {/* Tabla */}
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>{t("cxp.numDocRef")}</th>
                <th>{t("cxp.gastoPeriodo")}</th>
                <th>{t("cxp.proveedor")}</th>
                <th>{t("common.fecha")}</th>
                <th><div style={{ textAlign: "right" }}>{t("common.monto")}</div></th>
                <th className="text-center">{t("common.acciones")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ marginRight: 8, color: "#f97316" }} />
                  {t("cxp.cargandoFacturas")}
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-inbox"
                     style={{ marginRight: 8, fontSize: "1.2rem" }} />
                  {t("cxp.sinFacturas")}.{" "}
                  <Link to="/finanzas/cxp/facturas/agregar" style={{ color: "#f97316" }}>
                    {t("cxp.agregarPrimera")}
                  </Link>
                </td></tr>
              ) : items.map(fac => (
                <FacturaCxPRow key={fac.id} fac={fac} t={t}
                  puedeEmitirNota={puedeEmitirNota}
                  onVer={() => setDetalle(fac)}
                  onEdit={() => setEditando(fac)}
                  onDelete={() => setDeleting(fac)}
                  onEmitirNota={() => setEmitiendoNota(fac)} />
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer paginación */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }} />
                {t("common.cargando")}
              </span>
            ) : (
              <>
                <span>{count}</span> {t("cxp.facturas")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{Math.max(1, totalPages)}</span>
                {" · "}
                <span style={{ color: "var(--text-secondary)" }}>{t("common.totalFacturadoFiltro")}:</span>{" "}
                <span style={{ color: "#f97316", fontWeight: 700 }}>₡{formatCurrency(total)}</span>
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              {t("common.anterior")}
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {editando && (
        <EditFacturaModal fac={editando} t={t} onClose={() => setEditando(null)}
          onSaved={(msg, updated) => {
            showMsg(true, msg);
            setItems(prev => prev.map(f => f.id === updated.id ? updated : f));
            setEditando(null);
          }} />
      )}

      {/* Modal detalle */}
      {detalle && (
        <AccountingDetailModal
          onClose={() => setDetalle(null)}
          onEdit={() => { setEditando(detalle); setDetalle(null); }}
          icon="fa-file-invoice" iconColor="#f97316" iconBg="rgba(249,115,22,0.15)"
          title={`${t("cxp.tituloFacturaDetalle")} ${detalle.numero_doc_proveedor || detalle.numero_factura}`}
          badge={`N.° ${detalle.numero_factura}`}
          subtitle={`${t("cxp.fechaEmision")}: ${fmtDate(detalle.fecha_emision)} · ${t("cxp.fechaVencimiento")}: ${fmtDate(detalle.fecha_vencimiento) || "—"}`}
          infoTitle={t("cxp.informacionGeneral")} infoIcon="fa-circle-info"
          infoRows={[
            [t("cxp.numDocRef"),        detalle.numero_doc_proveedor || "—"],
            [t("cxp.numFacturaInt"),    detalle.numero_factura],
            [t("common.descripcion"),   detalle.concepto || "—"],
            [t("common.monto"), (detalle.moneda || "CRC").toUpperCase() === "USD"
              ? `$ ${Number(detalle.monto || 0).toFixed(2)}`
              : `₡${Number(detalle.monto || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              true],
            [t("cxp.fechaEmision"),      fmtDate(detalle.fecha_emision)],
            [t("cxp.fechaVencimiento"),  fmtDate(detalle.fecha_vencimiento) || "—"],
          ]}
          contableTitle={t("cxp.detalleContable")} contableIcon="fa-scale-balanced"
          debeLabel={t("cxp.cuentaGasto")} debeCuenta={detalle.cuenta_gasto?.nombre ?? "—"}
          debeColor="#f87171" debeBg="rgba(248,113,113,0.15)"
          haberLabel={t("cxp.cuentaProveedor")} haberCuenta={detalle.cuenta_proveedor?.nombre ?? "—"}
          haberColor="#60a5fa" haberBg="rgba(96,165,250,0.15)"
          montoFmt={(detalle.moneda || "CRC").toUpperCase() === "USD"
            ? `$ ${Number(detalle.monto || 0).toFixed(2)}`
            : `₡${Number(detalle.monto || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        />
      )}

      {deleting && (
        <DeleteFacturaCxPModal
          factura={deleting}
          loading={delLoad}
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
          onRecibosDeleted={fetchData}
        />
      )}

      {emitiendoNota && (
        <EmitirNotaCxPModal
          factura={emitiendoNota}
          onClose={() => setEmitiendoNota(null)}
          onSuccess={(nota) => {
            setEmitiendoNota(null);
            const ns = nota?.__tipo === "DEBITO" ? "notasDebito" : "notasCredito";
            showMsg(true, t(`${ns}.emitida`, { numero: nota?.numero_nota ?? "" }));
            fetchData();
          }}
        />
      )}
    </>
  );
}

// ── Modal eliminar factura CxP ────────────────────────────────────────────────
// Igual que CxC: si la factura tiene recibos vinculados, primero hay que
// eliminarlos uno por uno (con confirmación) y recién entonces se habilita
// el botón de eliminar la factura.
function DeleteFacturaCxPModal({ factura, loading, onConfirm, onCancel, onRecibosDeleted }) {
  const moneda = (factura.moneda || "CRC").toUpperCase();
  const sim    = moneda === "USD" ? "$ " : "₡ ";
  const monto  = Number(factura.monto || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  const [checking,       setChecking]       = useState(true);
  const [recibos,        setRecibos]        = useState([]);
  const [confirmRecibo,  setConfirmRecibo]  = useState(null);
  const [deletingRecibo, setDeletingRecibo] = useState(false);
  const [error,          setError]          = useState("");

  useEffect(() => {
    apiFetch(`/api/finance/cxp/${factura.id}/recibos-aplicados/`)
      .then((r) => (r.ok ? r.json() : { recibos: [] }))
      .then((d) => { setRecibos(d.recibos ?? []); setChecking(false); })
      .catch(() => setChecking(false));
  }, [factura]);

  async function handleDeleteRecibo(reciboId) {
    setDeletingRecibo(true);
    setError("");
    try {
      const res = await apiFetch(`/api/finance/reciboCxP/${reciboId}/`, { method: "DELETE" });
      if (res.status !== 204) throw new Error("Error al eliminar el recibo.");
      setRecibos((prev) => prev.filter((r) => r.recibo_id !== reciboId));
      onRecibosDeleted?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingRecibo(false);
      setConfirmRecibo(null);
    }
  }

  const canDelete = !checking && recibos.length === 0;

  function fmtFecha(iso) {
    if (!iso || iso.length < 10) return iso ?? "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }

  return (
    <>
      <div {...overlayClose(onCancel)} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
        zIndex: 1000, backdropFilter: "blur(3px)",
      }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 12, padding: "24px 24px 20px",
        width: "min(540px,94vw)", maxHeight: "88vh", overflowY: "auto", zIndex: 1001,
        boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ background: "rgba(248,113,113,0.15)", borderRadius: 8, padding: "8px 10px", color: "#f87171" }}>
              <i className="fa-solid fa-trash" style={{ fontSize: "1.1rem" }} />
            </div>
            <div>
              <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
                Eliminar Factura
              </h3>
              <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: "0.78rem" }}>
                {factura.numero_factura} — {sim}{monto}
              </p>
            </div>
          </div>
          <button onClick={onCancel} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "1.15rem", lineHeight: 1, padding: "2px 4px" }}>✕</button>
        </div>

        <div style={{ borderTop: "1px solid var(--border-card)", margin: "0 0 16px" }} />

        {checking ? (
          <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", padding: "8px 0" }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }} />
            Verificando recibos vinculados…
          </p>
        ) : recibos.length > 0 ? (
          <>
            {/* Warning: tiene recibos */}
            <div style={{
              background: "rgba(251,146,60,0.08)", border: "1px solid rgba(251,146,60,0.3)",
              borderLeft: "3px solid #fb923c", borderRadius: 6,
              padding: "9px 12px", color: "#fdba74", fontSize: "0.83rem",
              marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginTop: 2, flexShrink: 0 }} />
              <span>
                Esta factura tiene <strong>{recibos.length} recibo(s) de pago</strong> aplicado(s).
                Para eliminarla, primero eliminá los recibos — esto revierte los saldos bancarios
                y los asientos de pago.
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {recibos.map((r) => (
                <div key={r.recibo_id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: "var(--bg-input)", border: "1px solid var(--border-card)",
                  borderRadius: 6, padding: "8px 12px", gap: 8, flexWrap: "wrap",
                }}>
                  <div>
                    <span style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: "0.85rem" }}>
                      {r.numero_recibo}
                    </span>
                    <span style={{ color: "var(--text-secondary)", fontSize: "0.8rem", marginLeft: 8 }}>
                      Abono {sim}{Number(r.abono).toLocaleString("en-US", { minimumFractionDigits: 2 })} · {fmtFecha(r.fecha_pago)}
                    </span>
                    {r.multi_factura && (
                      <span style={{ marginLeft: 8, fontSize: "0.7rem", color: "#fb923c",
                        border: "1px solid rgba(251,146,60,0.4)", borderRadius: 999, padding: "1px 8px" }}
                        title="Este recibo paga varias facturas — al eliminarlo se revierten todas">
                        Paga varias facturas
                      </span>
                    )}
                  </div>
                  {confirmRecibo === r.recibo_id ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ color: "#fca5a5", fontSize: "0.76rem" }}>¿Confirmar?</span>
                      <button onClick={() => handleDeleteRecibo(r.recibo_id)} disabled={deletingRecibo}
                        style={{ padding: "3px 10px", background: "#ef4444", color: "#fff", border: "none",
                          borderRadius: 5, fontSize: "0.76rem", fontWeight: 600,
                          cursor: deletingRecibo ? "not-allowed" : "pointer" }}>
                        {deletingRecibo ? <i className="fa-solid fa-circle-notch fa-spin" /> : "Sí, eliminar"}
                      </button>
                      <button onClick={() => setConfirmRecibo(null)}
                        style={{ padding: "3px 8px", background: "transparent", color: "var(--text-muted)",
                          border: "1px solid var(--border-card)", borderRadius: 5, fontSize: "0.76rem", cursor: "pointer" }}>
                        No
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmRecibo(r.recibo_id)}
                      style={{ padding: "3px 10px", background: "rgba(248,113,113,0.15)", color: "#f87171",
                        border: "1px solid rgba(248,113,113,0.3)", borderRadius: 5,
                        fontSize: "0.76rem", fontWeight: 600, cursor: "pointer" }}>
                      <i className="fa-solid fa-trash" style={{ marginRight: 4 }} />
                      Eliminar recibo
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{
            background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
            borderLeft: "3px solid #f87171", borderRadius: 6,
            padding: "9px 12px", color: "#fca5a5", fontSize: "0.83rem",
            marginBottom: 16, display: "flex", gap: 8, alignItems: "flex-start",
          }}>
            <i className="fa-solid fa-circle-exclamation" style={{ marginTop: 2, flexShrink: 0 }} />
            <span>
              ¿Estás seguro de que deseas eliminar la factura <strong>{factura.numero_factura}</strong>?
              {" "}Se eliminará también su asiento contable.
              {" "}<strong>Esta acción no se puede deshacer.</strong>
            </span>
          </div>
        )}

        {error && (
          <div style={{ color: "#f87171", fontSize: "0.8rem", marginBottom: 12 }}>
            <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 5 }} />{error}
          </div>
        )}

        <div style={{ borderTop: "1px solid var(--border-card)", margin: "0 0 16px" }} />

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onCancel} disabled={loading} style={{
            padding: "8px 16px", background: "transparent",
            border: "1px solid var(--border-card)", borderRadius: 7,
            color: "var(--text-muted)", fontSize: "0.84rem", cursor: "pointer",
          }}>
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={!canDelete || loading} style={{
            padding: "8px 18px",
            background: !canDelete || loading ? "rgba(248,113,113,0.4)" : "#ef4444",
            color: "#fff", border: "none", borderRadius: 7,
            fontSize: "0.84rem", fontWeight: 600,
            cursor: !canDelete || loading ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 7,
          }}>
            {loading
              ? <><i className="fa-solid fa-circle-notch fa-spin" /> Eliminando…</>
              : <><i className="fa-solid fa-trash" /> Eliminar Factura</>
            }
          </button>
        </div>
      </div>
    </>
  );
}
